import {Button} from '@startlibs/components'
import {ConfirmDialog} from '@startlibs/form'
import {getColor} from '@startlibs/utils'
import React, {useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import styled from 'styled-components'
import {lazyProviderInfoSec, lazyUserInfo} from '../components/WithProvider'
import {clearSession} from '../hooks/useClearSession'
import {getJwt} from '../hooks/useJwt'
import {jwtGetFetcher, jwtPostFetcher} from '../utils/authFetch'

const AI_TERMS_URL = '/api/admin/aiTerms'
const AI_TERMS_NOT_ACCEPTED_STATUS = 570
const PURVIEW_AI_EMAIL_DOMAINS = ['@purview.net', '@healthmark-group.com']

const Intro = styled.p`
  font-size: 13px;
  color: ${getColor('gray90')};
  margin-bottom: 1.5rem;
`

const TermsScroll = styled.div`
  max-height: 25rem;
  overflow: auto;
  padding: 1.5rem;
  border-radius: 8px;
  background: #f2f2f2;
  font-size: 13px;
  line-height: 1.5;
  color: ${getColor('gray60')};
  p {
    white-space: pre-line;
  }
  p:last-child {
    margin-bottom: 0;
  }
`

// The term text is plain text; blank lines separate paragraphs.
const TermText = ({text}) => <TermsScroll>
  {(text || '').split(/\n{2,}/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
</TermsScroll>

export const AcceptTermsDialog = ({term, onAccept, onDecline}) => <ConfirmDialog
  title="Read the terms to proceed"
  css="max-width: 657px;"
  confirmChanges={false}
  isLoading={false}
  skipCloseIcon
  action={onAccept}
  keepOnSuccess
  confirm={<Button highlight>I read and agree with the terms</Button>}
  cancelButton={<Button onClick={onDecline}>I disagree</Button>}
>
  <Intro>Some parts of Expert View uses AI to process medical information. Please read the following terms:</Intro>
  <TermText text={term.text}/>
</ConfirmDialog>

// Same visibility rule as the AI "Details" button on medical records.
const canUseAiFeature = (providerInfoSec, userInfo) => {
  const aiVisibility = providerInfoSec.eveAiSettings?.visibility
  return aiVisibility === 'SHOW' ||
    (aiVisibility === 'PURVIEW' && PURVIEW_AI_EMAIL_DOMAINS.some(domain => userInfo.login?.endsWith(domain)))
}

// The server answers Ask AI with 570 when a newer term was published during the session.
const useOnTermsNotAccepted = (enabled, callback) => {
  useEffect(() => {
    if (!enabled) return
    const originalFetch = window.fetch
    window.fetch = (...args) => originalFetch(...args).then(response => {
      if (response.status === AI_TERMS_NOT_ACCEPTED_STATUS) callback()
      return response
    })
    return () => {
      window.fetch = originalFetch
    }
  }, [enabled, callback])
}

export const AcceptTermsGate = () => {
  const intl = useIntl()
  const providerInfoSec = lazyProviderInfoSec.read()
  const userInfo = lazyUserInfo.read()
  const canUseAi = canUseAiFeature(providerInfoSec, userInfo)
  const [term, setTerm] = useState(null)
  const jwt = getJwt()

  const loadDueTerm = useCallback(() =>
    jwtGetFetcher(jwt)(`${AI_TERMS_URL}/due`)
      .then(({terms}) => setTerm(terms?.[0] || null))
      // The server still gates Ask AI, so a failed check must not lock the admin out of the app.
      .catch(() => setTerm(null)),
  [jwt])

  useEffect(() => {
    if (canUseAi) loadDueTerm()
  }, [canUseAi, loadDueTerm])

  useOnTermsNotAccepted(canUseAi, loadDueTerm)

  const accept = () => jwtPostFetcher(jwt)(`${AI_TERMS_URL}/${term.id}/accept`, {})
    .then(() => {
      setTerm(null)
      return loadDueTerm()
    }, error => {
      // e.g. 400 when the term was replaced by a newer one while the dialog was open
      loadDueTerm()
      throw error
    })

  // Disagree: the decline is recorded, then the admin is signed out.
  const decline = () => jwtPostFetcher(jwt)(`${AI_TERMS_URL}/${term.id}/decline`, {})
    .catch(() => {})
    .then(() => jwtPostFetcher(jwt)(`/api/logout?locale=${intl.locale}`, ""))
    .catch(() => {})
    .finally(() => {
      clearSession()
      window.location = '/admin'
    })

  if (!canUseAi || !term) {
    return null
  }

  return <AcceptTermsDialog term={term} onAccept={accept} onDecline={decline}/>
}
